import { useCallback, useRef, useState } from "react";
import i18n from "i18next";
import { toast } from "sonner";
import { api } from "./axios";
import { apiError, retryMinutes } from "./apiError";
import { apiUrl } from "../config/backendFeatures";

/*
 * Phones on demand (turaq-backend docs/CONTACTS.md). With HIDE_PUBLIC_CONTACTS
 * on, public answers carry `has_phone` instead of `phone_number` / `phones` /
 * `whatsapp` / `email`; the number comes from:
 *   POST /api/property/:id/contact        → { phone, whatsapp }
 *   POST /api/v2/profiles/:ref/contact    → { phones: [{ number, label, is_primary }], whatsapp }
 * No sign-in needed. The server rate-limits it (429 CONTACT_RATE_LIMITED with
 * `retryAfter`), writes the phone reveal into analytics and — for a signed-in
 * visitor — the CRM lead, so the page sends no lead of its own any more.
 * 404 without a code: an older server without the route → the number that came
 * with the page data is used, as before.
 */

export interface RevealedContact {
  phone: string | null;
  whatsapp: string | null;
  /** All the profile's numbers (profiles only). */
  phones?: { number: string; label: string | null; is_primary: boolean }[];
  /** Came from the page data, not the server (older backend). */
  fromPage?: boolean;
}

export type ContactTarget = { kind: "listing"; id: number | string } | { kind: "profile"; ref: number | string };

const cache = new Map<string, RevealedContact>();
const keyOf = (t: ContactTarget) => (t.kind === "listing" ? `l:${t.id}` : `p:${t.ref}`);

/** `true` when the server says there is a number, or (older answer) the number itself is there. */
export function hasPhone(o: { has_phone?: boolean | null; phone_number?: string | null; phone?: string | null } | null | undefined): boolean {
  if (!o) return false;
  if (typeof o.has_phone === "boolean") return o.has_phone;
  return Boolean(o.phone_number || o.phone);
}

function lang(): "ru" | "kg" | "en" {
  const l = i18n.language || "ru";
  return l.startsWith("kg") || l.startsWith("ky") ? "kg" : l.startsWith("en") ? "en" : "ru";
}

const MSG = {
  ru: { limited: (m: number) => `Слишком много запросов, попробуйте через ${m} мин`, unavailable: "Контакт недоступен", failed: "Не удалось показать телефон. Попробуйте ещё раз." },
  kg: { limited: (m: number) => `Суроо өтө көп, ${m} мүнөттөн кийин кайра аракет кылыңыз`, unavailable: "Байланыш жеткиликсиз", failed: "Телефонду көрсөтүү болгон жок. Кайра аракет кылыңыз." },
  en: { limited: (m: number) => `Too many requests, try again in ${m} min`, unavailable: "Contact unavailable", failed: "Couldn't show the phone. Try again." },
};

/**
 * Ask the server for the number. Returns `null` on an error the user has been
 * told about; `fallback` is used when the route doesn't exist yet.
 */
export async function revealContact(target: ContactTarget, fallback?: { phone?: string | null; whatsapp?: string | null }): Promise<RevealedContact | null> {
  const key = keyOf(target);
  const hit = cache.get(key);
  if (hit) return hit;
  try {
    let out: RevealedContact;
    if (target.kind === "listing") {
      const { data } = await api.post<{ phone: string | null; whatsapp: string | null }>(apiUrl(`/api/property/${encodeURIComponent(String(target.id))}/contact`), {});
      out = { phone: data?.phone ?? null, whatsapp: data?.whatsapp ?? null };
    } else {
      const { data } = await api.post<{ phones: { number: string; label: string | null; is_primary: boolean }[]; whatsapp: string | null }>(
        apiUrl(`/api/v2/profiles/${encodeURIComponent(String(target.ref))}/contact`),
        {},
      );
      const phones = data?.phones ?? [];
      const primary = phones.find((p) => p.is_primary) ?? phones[0];
      out = { phone: primary?.number ?? null, whatsapp: data?.whatsapp ?? null, phones };
    }
    cache.set(key, out);
    return out;
  } catch (e) {
    const err = apiError(e);
    const L = MSG[lang()];
    if (err.missing) {
      if (!fallback?.phone) {
        toast.error(L.unavailable);
        return null;
      }
      const out = { phone: fallback.phone, whatsapp: fallback.whatsapp ?? fallback.phone, fromPage: true };
      cache.set(key, out);
      return out;
    }
    if (err.status === 429) toast.error(L.limited(retryMinutes(err.retryAfter)), { id: "contact-limit" });
    else if (err.status === 404) toast.error(L.unavailable);
    else toast.error(L.failed);
    return null;
  }
}

/** Reveal state for one listing / profile on a page. */
export function useContactReveal(target: ContactTarget | null, fallback?: { phone?: string | null; whatsapp?: string | null }) {
  const [contact, setContact] = useState<RevealedContact | null>(() => (target ? cache.get(keyOf(target)) ?? null : null));
  const [loading, setLoading] = useState(false);
  const busy = useRef(false);
  const fb = useRef(fallback);
  fb.current = fallback;
  const reveal = useCallback(async (): Promise<RevealedContact | null> => {
    if (!target) return null;
    if (busy.current) return null;
    busy.current = true;
    setLoading(true);
    try {
      const c = await revealContact(target, fb.current);
      if (c) setContact(c);
      return c;
    } finally {
      busy.current = false;
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target ? keyOf(target) : ""]);
  return { contact, loading, reveal };
}

/** Test hook: forget revealed numbers. */
export function __clearContactCache() {
  cache.clear();
}
